import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import View from './view';
import ArchiveTaskDialog from 'components/Tasks/Utils/ArchiveTaskDialog';
import TaskUtils from 'services/TaskUtils';
import _ from 'lodash';
import { useAppSelector } from 'careand-redux/store';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { GetStaff } from 'careand-redux/selectors';
import PerformanceUtils from 'services/PerformanceUtils';
import CalendarUtils from 'services/CalendarUtils';
import {
  useSpotlightPatientContext,
  useSpotlightTaskContext,
} from 'services/Spotlight/SpotlightAIContext';

let messagesEnd;

function scrollToBottom() {
  if (!!messagesEnd) messagesEnd.scrollIntoView();
}

const DEFAULT_OBJECT = {};

const TaskDetails = (props) => {
  const originalTask = props.task || DEFAULT_OBJECT;

  const defaultTask =
    !!props.task && Object.keys(props.task).length > 0
      ? {
          time: props.task.date,
          assigned_user_id: (props.staff || {}).id,
          patient_id: (props.patient || {}).id,
          ...props.task,
        }
      : {
          assigned_user_id: (props.staff || {}).id,
          patient_id: (props.patient || {}).id,
          date: moment().unix(),
          time: moment().unix(),
        };

  const [value, setValue] = useState(defaultTask);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [shouldMarkUnread, setShouldMarkUnread] = useState(true);

  const dispatch = useAppDispatch();

  const authentication = useAppSelector((state) => state.authentication);
  const patient = useAppSelector((state) => state.patients.byId[value.patient_id]);
  const assignedUser = useAppSelector((state) => GetStaff(state, value.assigned_user_id, false));
  const createdBy = useAppSelector((state) => GetStaff(state, value.author_id, false));
  const ownId = authentication.activeUserId;

  const isPrivateTask = ownId === assignedUser?.id;

  useSpotlightPatientContext(value.patient_id);
  useSpotlightTaskContext(undefined, defaultTask);

  useEffect(() => {
    let valueLen = (value.comments || []).length;
    let taskLen = (originalTask.comments || []).length;

    if (taskLen > valueLen) {
      updateField('comments', originalTask.comments);
    }
  }, [value.comments, originalTask.comments]);

  useEffect(() => {
    if (!!value.patient_id && !patient) {
      dispatch(api.Patients.getPatient({ id: value.patient_id }));
    }
  }, [value.patient_id, patient, dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [value.comments]);

  function setMessagesEnd(element) {
    messagesEnd = element;
  }

  const unreadComments = useMemo(
    () => TaskUtils.getUnreadComments({ comments: value.comments }, ownId),
    [value.comments, ownId],
  );
  const isUnread = useMemo(() => !(value.read_by || []).includes(ownId), [value, ownId]);

  useEffect(() => {
    if (shouldMarkUnread) {
      if (value.id && (unreadComments > 0 || isUnread)) {
        dispatch(api.Tasks.markTaskRead({ id: value.id }));
      }
    }
  }, [unreadComments, value.id, dispatch, isUnread, shouldMarkUnread]);

  function updateField(field, value) {
    if (field === 'date' && !CalendarUtils.isToday(moment.unix(value))) {
      updateField('time', CalendarUtils.militaryTimeToUnix(value, '0900'));
    }

    setValue((prevState) => {
      return {
        ...prevState,
        [field]: value,
      };
    });
  }

  function selectStaff(staff) {
    if (staff) updateField('assigned_user_id', staff.id);
    else {
      updateField('assigned_user_id', 'none');
    }
  }

  function selectPatient(patient) {
    if (patient) updateField('patient_id', patient.id);
    else updateField('patient_id', null);
  }

  async function onSave(vals, ignoreClose = false) {
    let values = PerformanceUtils.clone(vals);

    let { date, time } = values;

    if (!Number.isInteger(values.date)) date = moment(date).unix();
    if (!Number.isInteger(values.time)) time = moment(time).unix();

    date = moment.unix(date);
    time = moment.unix(time);
    date.hour(time.hour());
    date.minute(time.minute());

    for (let key in values) values[key] = values[key] || undefined;

    let data = {
      ...values,
      status: values.status || false,
      archived: values.archived || false,
      date: date.unix(),
      comments: (values.comments || []).map((comment) => {
        comment.read_by = _.uniq([...(comment.read_by || []), ownId]);
        return comment;
      }),
      clinic_id: patient?.clinic_id,
    };

    delete data.time;

    const options = { errorTitle: 'Task' };

    setSubmitting(true);
    let result = data.id
      ? await dispatch(api.Tasks.updateTask({ id: data.id, task: data }), options)
      : await dispatch(api.Tasks.addTask({ task: data }), options);
    setSubmitting(false);

    if (result) {
      setSubmitting(false);
      dispatch(api.Tasks.getAssignedTasks());

      if (!ignoreClose && props.onAddSuccess) props.onAddSuccess(result);
    }
  }

  function onArchive() {
    if (value.status) archive(false);
    else setArchiveDialog(true);
  }

  async function archive(markCompleted) {
    await onSave({ ...value, archived: true, status: markCompleted || value.status });
    setArchiveDialog(false);
  }

  async function saveComment(comment, notification) {
    setSubmitting(true);
    let originalComments = value.comments;
    let data = {
      author_id: ownId,
      text: comment,
      date: moment().unix(),
      read_by: [ownId],
      notifications_disabled: !notification,
    };
    let newComments = [...(originalComments || []), data];
    updateField('comments', newComments);
    let result = await dispatch(api.Tasks.addTaskComment({ id: value.id, taskComment: data }), {
      errorTitle: 'Add Comment',
    });
    if (result) {
      updateField('comments', result.comments);
    } else {
      updateField('comments', originalComments);
    }
    setSubmitting(false);
    return result;
  }

  async function deleteComment(comment) {
    setSubmitting(true);
    const commentIndex = value.comments.findIndex((c) => c === comment);
    if (commentIndex === -1) {
      console.error('Comment not found in comments array');
      setSubmitting(false);
      return;
    }
    let result = await dispatch(api.Tasks.removeTaskComment({ id: value.id, index: commentIndex }));
    if (result) {
      updateField('comments', result.comments);
    }
    setSubmitting(false);
  }

  function onClaim() {
    selectStaff(authentication.user);
  }

  async function deleteDocument(document) {
    let documents = (value.documents || []).filter((id) => id !== document.id);
    updateField('documents', documents);
    return onSave({ ...value, documents }, true);
  }

  async function onMarkUnread() {
    setShouldMarkUnread(false);
    let read_by = value.read_by?.filter((id) => id !== ownId);
    updateField('read_by', read_by);
    return onSave({ ...value, read_by }, false);
  }

  const isAssignedToMe = assignedUser && ownId === assignedUser.id;
  const isUnassigned = value.assigned_user_id === 'none';
  const isCreatedByMe = createdBy && ownId === createdBy.id;
  const canArchive =
    !value.archived && !submitting && (isAssignedToMe || isCreatedByMe || isUnassigned);

  return (
    <>
      {archiveDialog && (
        <ArchiveTaskDialog
          open={archiveDialog}
          loading={submitting}
          onArchive={archive}
          onCancel={() => setArchiveDialog(false)}
        />
      )}
      <View
        value={value}
        updateField={updateField}
        ownId={ownId}
        submitting={submitting}
        showPatient={!!props.showPatient || patient === undefined}
        onSelectStaff={selectStaff}
        onSelectPatient={selectPatient}
        onSave={onSave}
        onClose={props.onClose}
        patient={patient}
        staff={assignedUser}
        saveComment={saveComment}
        deleteComment={deleteComment}
        messagesEnd={messagesEnd}
        setMessagesEnd={setMessagesEnd}
        onClaim={onClaim}
        deleteDocument={deleteDocument}
        canArchive={canArchive}
        onArchive={canArchive ? onArchive : null}
        onMarkUnread={onMarkUnread}
        isPrivateTask={isPrivateTask}
      />
    </>
  );
};

TaskDetails.propTypes = {
  patient: PropTypes.object,
  staff: PropTypes.object,
  onAddSuccess: PropTypes.func,
  onClose: PropTypes.func,
  showPatient: PropTypes.bool,
  task: PropTypes.object,
};

export default TaskDetails;
